'use client';

import { requestLanguages } from '@/decodo/blocks/Scraper/constants/languages';
import {
  ResponseFormatView,
  responseFormatViews,
} from '@/decodo/blocks/Scraper/constants/response-formats';
import { useScraper } from '@/decodo/blocks/Scraper/state/context';
import CopyButton from '@/decodo/components/atoms/CodeBlock/CopyButton';
import { Toggle } from '@/decodo/components/atoms/Toggle';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { ResponseFormat, targetConfig } from '@smartproxy/sp-utils';

type RequestResponseFooterProps = {
  activeTab: 'request' | 'response';
  requestLanguageIndex: number;
  setRequestLanguageIndex: React.Dispatch<React.SetStateAction<number>>;
  responseFormatIndex: number;
  setResponseFormatIndex: React.Dispatch<React.SetStateAction<number>>;
  copyText?: string;
  hasResponse: boolean;
};

export const RequestResponseFooter = ({
  requestLanguageIndex,
  responseFormatIndex,
  setRequestLanguageIndex,
  setResponseFormatIndex,
  hasResponse,
  copyText,
  activeTab,
}: RequestResponseFooterProps) => {
  const { texts } = useTranslation();

  const responseFormatMap: Record<
    ResponseFormat,
    Record<ResponseFormatView, string>
  > = {
    json: {
      rich: texts.scraper.format_table,
      text: texts.scraper.format_json,
    },
    html: {
      rich: texts.scraper.format_live_preview,
      text: texts.scraper.format_html,
    },
  };

  const { target } = useScraper();

  const responseFormat = targetConfig[target.name].format;
  const formatOptions = responseFormatViews.map(
    (view) => responseFormatMap[responseFormat][view]
  );

  const shouldDisplayCopyButton =
    activeTab === 'request' || (activeTab === 'response' && hasResponse);

  return (
    <div className='dark relative flex h-16 shrink-0 justify-between px-6 pb-6 pt-2'>
      <div className='overflow-hidden'>
        {activeTab === 'request' && (
          <Toggle
            options={requestLanguages as unknown as string[]}
            size='s'
            selectedIndex={requestLanguageIndex}
            onValueChange={setRequestLanguageIndex}
            responsive={false}
          />
        )}
        {activeTab === 'response' && hasResponse && (
          <Toggle
            options={formatOptions}
            size='s'
            selectedIndex={responseFormatIndex}
            onValueChange={setResponseFormatIndex}
            responsive={false}
          />
        )}
      </div>
      {shouldDisplayCopyButton && <CopyButton copyText={copyText || ''} />}
    </div>
  );
};
